import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    CalendarMonth,
    Inventory,
    ShoppingCart,
    LocalShipping,
    CheckCircleOutline,
    ErrorOutline,
    Group,
    ArrowForwardIos
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェア引っ越しの魅力と注意点' },
    { id: 'schedule', label: '1. 引っ越しスケジュールの立て方' },
    { id: 'declutter', label: '2. 荷物整理術と断捨離テクニック' },
    { id: 'shared-items', label: '3. 共同購入品の扱い方と分担' },
    { id: 'moving-company', label: '4. 引っ越し業者の選び方と依頼ポイント' },
    { id: 'summary', label: 'まとめ＆引っ越し成功のコツ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888',
                                        fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', mr: 1, fontSize: '1.15rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left', p: 0 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア引っ越しの魅力と注意点
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    新しいスタートを切る「引っ越し」。それがルームシェアとなると、一人暮らしとは違うワクワクや独特の課題がついてきます。<b>引っ越し準備の手順やコツ</b>を押さえておくことで、トラブルを未然に防ぎ、快適な新生活を始めることができます。
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    本記事では、<Chip label="引っ越しスケジュール" color="primary" size="small" variant="outlined" sx={{ mx: 0.5 }} /> 
                    <Chip label="荷物整理術" color="primary" size="small" variant="outlined" sx={{ mx: 0.5 }} />
                    <Chip label="共同購入品" color="primary" size="small" variant="outlined" sx={{ mx: 0.5 }} />
                    <Chip label="業者の選び方" color="primary" size="small" variant="outlined" sx={{ mx: 0.5 }} />
                    など、ルームシェアならではの引っ越し準備を徹底解説します！
                </Typography>
            </Box>

            {/* 画像1 */}
            <Box sx={{ mb: 4 }}>
                <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    
                </Paper>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                    新生活のスタートに向けてルームメイト同士で協力し合う様子。事前の計画が安心のカギに
                </Typography>
            </Box>

            {/* スケジュール */}
            <Box id="schedule" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    1. 引っ越しスケジュールの立て方
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={7}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            引っ越し成功の秘訣は「スケジュール管理」にあり！ルームシェアの場合は<b>複数人の予定調整</b>や<b>役割分担</b>が欠かせません。以下のポイントを押さえて計画を立てましょう。
                        </Typography>
                        <List sx={{ pl: 1 }}>
                            <ListItem>
                                <ListItemIcon><CalendarMonth color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>約1ヵ月前</b>}
                                    secondary="新居の契約、現住居の退去連絡、ルームメイト全員での引っ越し日程決定"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><Inventory color="action" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>2〜3週間前</b>}
                                    secondary="荷造り開始、不要品の整理、粗大ゴミの手配"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ShoppingCart color="success" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>1〜2週間前</b>}
                                    secondary="共同購入品リスト作成・購入、水道・電気・ネットの手続き"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><LocalShipping color="secondary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>直前〜当日</b>}
                                    secondary="最終確認、鍵の受け渡し、引っ越し作業"
                                />
                            </ListItem>
                        </List>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            ルームメイトとのグループチャットやカレンダー共有アプリの活用もおすすめです！
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#f9fbe7', borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                <Group sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                                スケジュール調整のコツ
                            </Typography>
                            <List dense>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="全員の都合を早めに確認" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="役割分担を明確に決める" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="ToDoリストでタスクを可視化" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="タスク管理アプリやGoogleカレンダーを活用" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 画像2 */}
            <Box sx={{ mb: 5 }}>
                <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    <img
                        src="/images/blog/packing-boxes_roomshare-moving-guide.jpg"
                        alt="段ボール箱や衣装ケースが整然と並ぶ部屋で、2人のルームメイトが協力して荷物を梱包している。室内は明るく、作業に集中している様子。"
                        style={{ width: '100%', display: 'block', maxHeight: 260, objectFit: 'cover' }}
                    />
                </Paper>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                    荷造りは協力して効率アップ。役割分担がトラブル防止にもつながります
                </Typography>
            </Box>

            {/* 荷物整理術 */}
            <Box id="declutter" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    2. 荷物整理術と断捨離テクニック
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            ルームシェアの引っ越しで意外と重要なのが「荷物の整理」。共有スペースが限られているため、<b>コンパクトで効率的な荷物管理</b>が求められます。
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            断捨離のタイミングとしても最適。以下のポイントを参考にして、荷物をスマートに整理しましょう。
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                <ListItemText primary="持ち物をカテゴリごとに仕分け" secondary="衣類・書籍・キッチン用品など、カテゴリ毎に分けて梱包" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                <ListItemText primary="1年以上使っていない物は処分" secondary="リサイクル・フリマアプリ・寄付も活用" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                <ListItemText primary="共有物と個人所有物を明確に" secondary="誤って捨てないよう、タグやシールで区別" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                <ListItemText primary="ラベル付けで梱包効率UP" secondary="誰の物・中身が一目でわかるように" />
                            </ListItem>
                        </List>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            収納グッズや圧縮袋の活用で省スペース化も実現！
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                                荷物整理のチェックリスト
                            </Typography>
                            <List dense>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon><ArrowForwardIos fontSize="small" sx={{ color: '#64b5f6' }} /></ListItemIcon>
                                    <ListItemText primary="捨てる・売る・譲る・残すを仕分け" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon><ArrowForwardIos fontSize="small" sx={{ color: '#64b5f6' }} /></ListItemIcon>
                                    <ListItemText primary="大型ごみは自治体へ早めに連絡" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon><ArrowForwardIos fontSize="small" sx={{ color: '#64b5f6' }} /></ListItemIcon>
                                    <ListItemText primary="梱包資材は多めに用意" />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon><ArrowForwardIos fontSize="small" sx={{ color: '#64b5f6' }} /></ListItemIcon>
                                    <ListItemText primary="共有スペース分の収納計画も忘れずに" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 画像3 */}
            <Box sx={{ mb: 5 }}>
                <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    
                </Paper>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                    共同購入品はリストアップと話し合いがスムーズなシェア生活のコツ
                </Typography>
            </Box>

            {/* 共同購入品 */}
            <Box id="shared-items" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    3. 共同購入品の扱い方と分担
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア生活では「共有で使うモノ」の買い方や支払い方法を決めておくことが大切です。食器、調理家電、洗剤などの<b>共同購入品リスト</b>を作成し、負担や所有権のトラブルを防ぎましょう。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                <ShoppingCart sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                                共同購入品の例
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="調理家電（炊飯器・電子レンジ）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="調理器具・食器類" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="洗剤・トイレットペーパーなどの消耗品" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="掃除機などの家電" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="Wi-Fiルーター" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                <Group sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                                賢い分担・購入のコツ
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="事前にリスト化し、担当を決める" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="支払いは割り勘アプリや現金で精算" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="購入品の所有権ルールを明文化" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="消耗品は定期的に分担して購入" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    「誰がどの家電を持ち寄るか」なども事前に話し合っておくと、余計な出費や誤解を防げます。
                </Typography>
            </Box>

            {/* 画像4 */}
            <Box sx={{ mb: 5 }}>
                <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    
                </Paper>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                    引っ越し業者の選び方も大切。複数見積もりでコスパと安心を両立しましょう
                </Typography>
            </Box>

            {/* 業者選び */}
            <Box id="moving-company" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    4. 引っ越し業者の選び方と依頼ポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    荷物が多くなりがちなルームシェア。<b>引っ越し業者の選定</b>は、コスト・安心・柔軟性のバランスがカギです。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#e1f5fe', borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                                業者選びのチェックポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="複数社から見積もりを取る" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="「ルームシェア」と伝え、料金や注意点を確認" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="荷物量・大型家具の有無・新居の間取りを事前に伝える" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                                    <ListItemText primary="保険対応やキャンセル規定も要チェック" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#fffde7', borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                <ErrorOutline sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                                よくあるトラブル例
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="見積もりより高額請求" secondary="荷物量や搬出条件の伝達ミスに注意" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="共有物の紛失・破損" secondary="保険内容を事前に確認し、貴重品は自分で運ぶ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="warning" /></ListItemIcon>
                                    <ListItemText primary="搬入・搬出時の近隣トラブル" secondary="事前に管理会社や近隣へ連絡を入れる" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    業者には「ルームシェア」と必ず伝え、担当や連絡先も共有しておくと安心です。
                </Typography>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ＆引っ越し成功のコツ
                </Typography>
                <Paper elevation={1} sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        ルームシェア引っ越しのポイント
                    </Typography>
                    <List sx={{ mb: 1 }}>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="早めのスケジュール調整と役割分担が成功のカギ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="荷物整理は「断捨離」の意識でスマートに" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="共同購入品や費用分担は事前の話し合いでトラブル回避" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="業者選びは情報収集と比較が重要" />
                        </ListItem>
                    </List>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        新しいルームシェア生活は「準備」で大きく変わります。少しの工夫で、快適な共同生活と素敵な新生活をスタートさせましょう！
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-moving-guide',
    slug: 'roomshare-moving-guide',
    title: 'ルームシェアの「引っ越し」準備と手順ガイド',
    excerpt: '引っ越しスケジュール\n荷物整理術\n共同購入品の扱い\n業者の選び方',
    coverImage: '/images/blog/cover-roomshare-moving_roomshare-moving-guide.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目調のインテリアと観葉植物が配置され、温かい雰囲気が感じられる空間で新生活の期待が高まっている場面。',
    publishedAt: '2024-06-12T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '引っ越し', '生活ノウハウ'],
    tags: ['引っ越し準備', '荷物整理', '共同購入', '業者選び', 'ルームシェア'],
    author: {
        name: '佐藤 真理',
        avatar: '/images/avatars/mari-sato.jpg',
        bio: 'ルームシェア歴10年。シェアハウス・共同生活の実体験と多数のコンサル経験をもとに、快適な住まいづくりと引っ越しノウハウを発信しています。',
        profilePicture: '/images/avatars/mari-sato.jpg'
    },
    viewCount: 1200,
    contentComponent: PostContent
};

export default post;